import { z } from 'zod';

/**
 * Zod schemas for the BP-04 `/staff`, `/availability`, `/bookings` and `/messaging`
 * responses (ARCHITECTURE §3 Validation: "Pydantic on the api; Zod on the web from the
 * same OpenAPI").
 *
 * Hand-written rather than derived from `src/lib/api/schema.d.ts`, for the reason
 * `features/dpdp/schemas.ts` gives: that file is generated in CI from an OpenAPI
 * document that only contains a route once `app/main.py` mounts it, and BP-04 does not
 * edit `app/main.py`.
 *
 * **No client PII crosses this boundary.** `BookingOut` carries `client_id` and nothing
 * else about the person (BP-04 §9); a `MessageOut` carries the *mask*. There is no
 * branch in this app that unmasks either — the value simply never arrives.
 */

/** ARCHITECTURE §4's `booking.status`, in the order R1 walks them. */
export const bookingStatuses = [
  'requested',
  'confirmed',
  'checked_in',
  'in_service',
  'completed',
  'no_show',
  'cancelled',
] as const;

/** ARCHITECTURE §4's `booking.source`. */
export const bookingSources = [
  'walkin',
  'web',
  'app',
  'staff',
  'whatsapp',
  'ar',
  'google',
] as const;

export const bookingStatusSchema = z.enum(bookingStatuses);
export const bookingSourceSchema = z.enum(bookingSources);

/** One roster row (§5 `StaffOut`). */
export const staffSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  outlet_id: z.string(),
  skills: z.array(z.string()),
  skill_levels: z.record(z.string(), z.enum(['trainee', 'competent', 'expert'])).default({}),
  colour: z.string(),
  user_id: z.string().nullable(),
  is_bookable: z.boolean(),
  sort_order: z.number(),
  active: z.boolean(),
});

/** One weekday row of the template; several per weekday is a split shift (§3). */
export const scheduleRowSchema = z.object({
  weekday: z.number(),
  start_time: z.string(),
  end_time: z.string(),
  valid_from: z.string(),
  valid_to: z.string().nullable(),
});

export const scheduleWeekSchema = z.object({ rows: z.array(scheduleRowSchema) });

/** One absence (§5 `TimeOffOut`). */
export const timeOffSchema = z.object({
  id: z.string(),
  staff_id: z.string(),
  start_at: z.string(),
  end_at: z.string(),
  reason: z.string().nullable(),
});

/** One bookable start (§5 `SlotOut`, R11e). */
export const slotSchema = z.object({
  start_at: z.string(),
  end_at: z.string(),
  staff_id: z.string(),
  station_id: z.string().nullable(),
});

export const slotsSchema = z.object({ date: z.string(), slots: z.array(slotSchema) });

/** One item of a booking (§5 `BookingItemOut`). */
export const bookingItemSchema = z.object({
  id: z.string(),
  service_id: z.string(),
  addon_ids: z.array(z.string()),
  staff_id: z.string(),
  station_id: z.string().nullable(),
  start_at: z.string(),
  end_at: z.string(),
  buffer_min: z.number(),
  is_active: z.boolean(),
  price_snapshot_paise: z.number(),
  any_staff: z.boolean(),
});

/** The scheduling policy R6 snapshots onto every booking. */
export const schedulingPolicySchema = z.object({
  auto_confirm: z.boolean(),
  buffer_min: z.number(),
  slot_step_min: z.number(),
  client_cutoff_hours: z.number(),
  client_max_reschedules: z.number(),
  min_lead_min: z.number(),
  max_advance_days: z.number(),
  no_show_grace_min: z.number(),
  no_show_undo_min: z.number(),
});

/** One booking (§5 `BookingOut`). */
export const bookingSchema = z.object({
  id: z.string(),
  ref: z.string(),
  status: bookingStatusSchema,
  source: bookingSourceSchema,
  client_id: z.string().nullable(),
  outlet_id: z.string(),
  notes: z.string().nullable(),
  cancel_reason: z.string().nullable(),
  cancelled_by: z.string().nullable(),
  reschedule_count: z.number(),
  policy_snapshot: schedulingPolicySchema,
  items: z.array(bookingItemSchema),
  allowed_targets: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
});

export const bookingPageSchema = z.object({
  items: z.array(bookingSchema),
  next_cursor: z.string().nullable(),
});

/** §5's `SchedulingSettingsOut`. */
export const schedulingSettingsSchema = z.object({
  policy: schedulingPolicySchema,
  writable: z.boolean(),
});

/** One message template (§5 `TemplateOut`). */
export const templateSchema = z.object({
  id: z.string(),
  key: z.string(),
  channel: z.string(),
  locale: z.string(),
  category: z.string(),
  body: z.string(),
  vars: z.array(z.string()),
  provider_template_id: z.string().nullable(),
  provider_status: z.string().nullable(),
  active: z.boolean(),
  /**
   * BP-18 §3's three. `null` on every template that is not `sms`, and on
   * an `sms` one nobody has registered with DLT — which the adapter
   * refuses to send rather than letting the operator scrub it.
   *
   * `dlt_category` is India's content category and **not** `category`,
   * which is Meta's and is BP-04 R18's rate-card key. A template can be
   * Meta-`utility` and DLT-`transactional`.
   */
  dlt_template_id: z.string().nullable(),
  dlt_header: z.string().nullable(),
  dlt_category: z.string().nullable(),
});

/** One ledger row, masked (§5 `MessageOut`). Note the absent `to_e164`. */
export const messageSchema = z.object({
  id: z.string(),
  client_id: z.string().nullable(),
  to_e164_masked: z.string().nullable(),
  channel: z.string(),
  direction: z.string(),
  template_key: z.string().nullable(),
  vars_masked: z.record(z.string(), z.string()),
  status: z.string(),
  category: z.string(),
  cost_paise: z.number(),
  rate_version: z.string().nullable(),
  ref_type: z.string().nullable(),
  ref_id: z.string().nullable(),
  provider_msg_id: z.string().nullable(),
  body_inbound: z.string().nullable(),
  sent_at: z.string().nullable(),
  delivered_at: z.string().nullable(),
  created_at: z.string(),
});

export const messagePageSchema = z.object({
  items: z.array(messageSchema),
  next_cursor: z.string().nullable(),
});

/** §5's `RelayConfigOut` (R22). */
export const relayConfigSchema = z.object({
  mode: z.enum(['own_number', 'relay_26ad']),
  whatsapp_phone_number_id: z.string().nullable(),
  waba_id: z.string().nullable(),
  sender_display: z.string().nullable(),
  webhook_verify_token_ref: z.string().nullable(),
});

/** R18's rate card, in paise. */
export const rateCardSchema = z.object({
  version: z.string(),
  utility_paise: z.number(),
  authentication_paise: z.number(),
  marketing_paise: z.number(),
  service_paise: z.number(),
});

/** R16's quiet-hours window, in the tenant timezone. */
export const quietHoursSchema = z.object({ start: z.string(), end: z.string() });

export const smtpSchema = z.object({
  host: z.string(),
  port: z.number(),
  username: z.string(),
  from_email: z.string(),
  from_name: z.string(),
  starttls: z.boolean(),
});

/**
 * BP-18 §3's `messaging.sms` — the DLT configuration, none of it secret.
 *
 * The MSG91 auth key is a `tenant_secret` and is deliberately absent:
 * this object comes back from `GET /settings/messaging`, and a
 * credential on the wire to every screen is the thing BP-01 R10 exists
 * to prevent.
 */
export const smsSchema = z.object({
  enabled: z.boolean(),
  provider: z.string(),
  dlt_entity_id: z.string(),
  /** Every sender id this salon registered; a template names which one. */
  headers: z.array(z.string()),
  /** Hosts DLT has whitelisted — hosts, because that is what it registers. */
  url_hosts: z.array(z.string()),
  fallback_on_whatsapp_failure: z.boolean(),
});

/** §5's `MessagingSettingsOut`. */
export const messagingSettingsSchema = z.object({
  rate_card: rateCardSchema,
  quiet_hours: quietHoursSchema,
  smtp: smtpSchema,
  sms: smsSchema,
  whatsapp_enabled: z.boolean(),
  /**
   * BP-11 rule R7's channel list, in the order a reminder fans out.
   *
   * On this screen because it is the same question the rest of it answers —
   * how a salon's messages go out — and because the owner who turns push on
   * is the owner who reads what a message costs.
   */
  reminder_channels: z.array(z.string()),
});

export type Staff = z.infer<typeof staffSchema>;
export type ScheduleRow = z.infer<typeof scheduleRowSchema>;
export type ScheduleWeek = z.infer<typeof scheduleWeekSchema>;
export type TimeOff = z.infer<typeof timeOffSchema>;
export type Slot = z.infer<typeof slotSchema>;
export type Slots = z.infer<typeof slotsSchema>;
export type BookingItem = z.infer<typeof bookingItemSchema>;
export type Booking = z.infer<typeof bookingSchema>;
export type BookingPage = z.infer<typeof bookingPageSchema>;
export type BookingStatus = z.infer<typeof bookingStatusSchema>;
export type BookingSource = z.infer<typeof bookingSourceSchema>;
export type SchedulingPolicy = z.infer<typeof schedulingPolicySchema>;
export type SchedulingSettings = z.infer<typeof schedulingSettingsSchema>;
export type MessageTemplate = z.infer<typeof templateSchema>;
export type Message = z.infer<typeof messageSchema>;
export type MessagePage = z.infer<typeof messagePageSchema>;
export type RelayConfig = z.infer<typeof relayConfigSchema>;
export type RateCard = z.infer<typeof rateCardSchema>;
export type QuietHours = z.infer<typeof quietHoursSchema>;
export type SmtpConfig = z.infer<typeof smtpSchema>;
export type MessagingSettings = z.infer<typeof messagingSettingsSchema>;
